import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import { reset } from 'redux-form';

import { FormCreateApiKeyData } from 'dashboard/forms/FormCreateApiKey';
import { FormExpireApiKeyData } from 'dashboard/forms/FormExpireApiKey';
import { FormPasswordConfirmationModalData } from 'dashboard/forms/FormPasswordConfirmationModal';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { ApiKey } from 'dashboard/models/api-key';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { expireApiKey } from 'dashboard/redux/thunk-actions/api-key';
import { loadApiKeyList } from 'dashboard/redux/thunk-actions/api-keys';
import DashboardApiKeysView from 'dashboard/views/DashboardApiKeysView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardApiKeysRoute = (props: PropsFromRedux) => {
  const navigate = useNavigate();

  const { loading: isLoadingApiKeys, callback: loadApiKeys } = useApiLoader({
    loader: () => props.loadApiKeyList({ currentEnvironment: props.currentEnvironment }),
    deps: [props.currentEnvironment],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load API keys.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  // ==============================
  // Create API Key
  // ==============================
  const { error: createApiKeyError, callback: handleCreateApiKey } = useApiCallback(
    async (data: FormCreateApiKeyData, meta?: FormPasswordConfirmationModalData) => {
      const response = await SessionClientDashboard.postResourceCreate<ApiKey>(
        {
          type: ApiModel.ApiKey,
          attributes: {
            'environment-id': props.currentEnvironment.id,
            name: data['name'],
            note: data['note'],
          },
        },
        {
          meta: {
            // Note this will be blank if organization has SAML enabled.
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'password-confirmation': meta?.['password-confirmation'],
          },
        },
      );

      loadApiKeys();
      props.showMessage('Successfully created new API key', MessageType.Success);
      navigate(`/dashboard/api-keys/${response.data.id}`);
    },
    [props.currentEnvironment],
  );

  useApiErrorHandler({
    error: createApiKeyError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Error encountered when creating new API key.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  // ==============================
  // Show API Key
  // ==============================
  const handleShowApiKey = (apiKey: ApiKey) => {
    navigate(`/dashboard/api-keys/${apiKey.id}`);
  };

  // ==============================
  // Expire API Key
  // ==============================
  const { error: expireApiKeyError, callback: handleExpireApiKey } = useApiCallback(
    async (
      apiKey: ApiKey,
      data: FormExpireApiKeyData,
      meta?: FormPasswordConfirmationModalData,
    ) => {
      await props.expireApiKey(apiKey.id, {
        'expires-in-seconds': data['expires-in-seconds'],
        // Note this will be blank if organization has SAML enabled.
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'password-confirmation': meta?.['password-confirmation'],
      });

      loadApiKeys();
      props.showMessage(
        'API Key sucessfully updated. Don’t forget to update your API calls to match any changes.',
        MessageType.Success,
      );
    },
    [props.expireApiKey, props.showMessage, history],
  );

  useApiErrorHandler({
    error: expireApiKeyError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Something went wrong when updating your API key', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  return (
    <DashboardApiKeysView
      currentEnvironment={props.currentEnvironment}
      organization={props.organization}
      apiKeys={props.apiKeys}
      isLoadingApiKeys={isLoadingApiKeys}
      handleCreateApiKey={handleCreateApiKey}
      handleShowApiKey={handleShowApiKey}
      handleExpireApiKey={handleExpireApiKey}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    organization: state.organization.organization,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    apiKeys: state.apiKeys.apiKeys,
  };
};

const mapDispatchToProps = {
  loadApiKeyList,
  expireApiKey,
  showMessage,
  reset,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardApiKeysRoute);
